import { Box, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";

import { StreetlightStatusIndicator } from "../../molecules/streetlight-status-indicator/streetlight-status-indicator";
import { StreetlightStatusItem } from "../../molecules/streetlight-status-item/streetlight-status-item";
import {
  isStreetlightStatusType,
  STREETLIGHT_STATUS_TYPE,
} from "../../types/streetlight-status-type";
import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMSmallStatusProps extends Omit<PMBaseCardProps, "children"> {
  /** (`status`): The primary status to display in the center of the card. */
  status: STREETLIGHT_STATUS_TYPE | string;
  /** (`position`): The position of the traffic light indicator. Only to be used in conjuction with a "string" as the status. Should be either 1, 2 or 3 three! */
  position?: number;
  /** (`normal` | `large`): The size of the primary status indicator. Can be "large" or "normal". Default: "normal" */
  primaryStatusIndicatorSize?: "normal" | "large";
  /** ({label: string; status: STREETLIGHT_STATUS_TYPE}[]): The sub statuses to be shown below the primary status.
   *  Can show up to four sub statuses. Default: "row" */
  subStatuses?: {
    name: string;
    status: STREETLIGHT_STATUS_TYPE | string | undefined;
  }[];
  /** (`row` | `column`) Shows the sub statuses either in a row or in a column. */
  subStatusesDisplay?: "row" | "column";
  /** (`string`): The title to display in the card header. */
  title: string;
  /** (`string`): Icon to display in the header. If a string is provided, it is resolved to a Material-UI icon. */
  icon?: string;
  /** (`string`): Optional font size for the sub status labels. Defaults to theme.typography.button.fontSize. */
  subStatusFontSize?: string;
}

/**
 * PMSmallStatus is a component that extends PMBaseCard to display either a single, large traffic light status indicator
 * or optionally up to four sub status indicators, centered in a card with optional icons in the header.
 *
 * Props:
 * - status: The primary status to display in the center of the card.
 * - primaryStatusIndicatorSize: The size of the primary status indicator. Can be "large" or "normal". Default: "normal"
 * - subStatuses: The sub statuses to be shown below the primary status. Can show up to four sub statuses.
 * - subStatusesDisplay: Shows the sub statuses either in a row or in a column. Default: "row"
 * - icon: An optional icon to display in the card header, resolved if provided as a string.
 * - title: The title to display in the card header.
 * - restCardProps: Additional props to be passed to the PMBaseCard component.
 */

export const PMSmallStatus: PimoReactComponent<PMSmallStatusProps> = ({
  status,
  primaryStatusIndicatorSize = "normal",
  subStatuses,
  subStatusesDisplay = "row",
  icon = "CheckCircleOutlined",
  title,
  position,
  subStatusFontSize,
  ...restCardProps
}) => {
  let pos = position;
  if (pos && pos < 1) {
    pos = 1;
  }
  if (pos && pos > 3) {
    pos = 3;
  }

  const isRow = subStatusesDisplay === "row";
  const hasSubStatuses = subStatuses;
  const theme = useTheme();
  const PrimaryStatusIndicator = (
    <StreetlightStatusIndicator
      status={status}
      position={pos}
      borderColor={theme.palette.grey[500]}
      size={primaryStatusIndicatorSize}
    />
  );

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={false}
      showMenuIcon={false}
      rightSlotOverride={hasSubStatuses && PrimaryStatusIndicator}
      {...restCardProps}
    >
      {!hasSubStatuses && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 2,
            pt: 0.5,
          }}
        >
          {PrimaryStatusIndicator}
        </Box>
      )}
      <Box
        sx={{
          p: 0,
          pl: 0.3,
          mt: isRow ? -1.5 : -2.5,
          display: "grid",
          ...(isRow
            ? { gridTemplateColumns: "repeat(3, 1fr)", gridGap: 32 }
            : {
                gridTemplateRows: "repeat(4, 1fr)",
                gridGap: 0,
                alignItems: "center",
                justifyItems: "stretch",
              }),
        }}
      >
        {(subStatuses ?? []).map((subStatus, index) => {
          const isEnumStatus = isStreetlightStatusType(
            String(subStatus.status)
          );

          let subPosition = index + 1;
          if (subPosition < 1) {
            subPosition = 1;
          }
          if (subPosition > 3) {
            subPosition = 3;
          }

          return (
            <StreetlightStatusItem
              key={subStatus.name}
              position={
                (!isEnumStatus ? (subPosition as 1 | 2 | 3) : undefined) as
                  | 1
                  | 2
                  | 3
                  | undefined
              }
              {...subStatus}
              areElementsStacked={isRow}
              sx={{
                border: `1px solid ${theme.palette.grey[500]}`,
              }}
              fontSize={
                subStatusFontSize ??
                theme.typography.button.fontSize?.toString()
              }
            />
          );
        })}
      </Box>
    </PMBaseCard>
  );
};

export default PMSmallStatus;
